// Sound notification utilities for kitchen notifications

export class SoundNotifications {
  private static instance: SoundNotifications;
  private audioContext: AudioContext | null = null;
  private sounds: Map<string, HTMLAudioElement> = new Map();
  private enabled = true;
  private volume = 0.7; // Default volume (0.0 - 1.0)

  private constructor() {
    this.initializeAudioContext();
  }

  static getInstance(): SoundNotifications {
    if (!SoundNotifications.instance) {
      SoundNotifications.instance = new SoundNotifications();
    }
    return SoundNotifications.instance;
  }

  private initializeAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    } catch (error) {
      console.warn('Audio context not supported:', error);
    }
  }

  // Create a simple beep sound programmatically
  private createBeepSound(frequency: number, duration: number): Promise<void> {
    return new Promise((resolve) => {
      if (!this.audioContext) {
        resolve();
        return;
      }

      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration);

      setTimeout(() => resolve(), duration * 1000);
    });
  }

  /**
   * Sets the volume level for notification sounds.
   * Updates both the default volume and all cached audio elements.
   * @param level Volume level between 0.0 (silent) and 1.0 (maximum)
   */
  setVolume(level: number): void {
    this.volume = Math.max(0, Math.min(1, level)); // Clamp between 0 and 1
    
    // Update volume for all cached audio elements
    this.sounds.forEach(audio => {
      audio.volume = this.volume;
    });
  }

  /**
   * Gets the current volume level.
   * @returns Current volume level (0.0 - 1.0)
   */
  getVolume(): number {
    return this.volume;
  }

  /**
   * Loads an audio file from the specified URL and caches it in the `sounds` Map.
   * If the audio file has already been loaded, it is retrieved from the cache.
   * Otherwise, the file is loaded and stored in the cache for future use.
   * 
   * Handles both successful loads and error cases:
   * - On successful load, resolves with the HTMLAudioElement and caches it.
   * - On error (e.g., file cannot be loaded), rejects the promise with an error.
   * 
   * @param url The URL of the audio file to load.
   * @returns A promise that resolves with the loaded HTMLAudioElement, or rejects if loading fails.
   */
  private async loadAudioFile(url: string): Promise<HTMLAudioElement> {
    return new Promise((resolve, reject) => {
      if (this.sounds.has(url)) {
        resolve(this.sounds.get(url)!);
        return;
      }

      const audio = new Audio(url);
      audio.preload = 'auto';
      audio.volume = this.volume; // Use configurable volume

      const onLoad = () => {
        this.sounds.set(url, audio);
        audio.removeEventListener('canplaythrough', onLoad);
        audio.removeEventListener('error', onError);
        resolve(audio);
      };

      const onError = () => {
        audio.removeEventListener('canplaythrough', onLoad);
        audio.removeEventListener('error', onError);
        reject(new Error(`Failed to load audio file: ${url}`));
      };

      audio.addEventListener('canplaythrough', onLoad);
      audio.addEventListener('error', onError);
    });
  }

  // Play MP3 audio file
  private async playMP3Sound(url: string): Promise<void> {
    const audio = await this.loadAudioFile(url);
    audio.currentTime = 0; // Reset to start
    
    try {
      await audio.play();
    } catch (error) {
      console.warn(`Failed to play audio file: ${url}`, error);
      throw error;
    }
  }

  // Play fallback beep sequence for new orders
  private async playNewOrderBeepSequence(): Promise<void> {
    await this.createBeepSound(800, 0.2);
    await new Promise(resolve => setTimeout(resolve, 100));
    await this.createBeepSound(1000, 0.2);
    await new Promise(resolve => setTimeout(resolve, 100));
    await this.createBeepSound(800, 0.2);
  }

  // Play new order notification
  async playNewOrderNotification(): Promise<void> {
    if (!this.enabled) return;

    try {
      await this.playMP3Sound('/sounds/new-order.mp3');
    } catch (mp3Error) {
      console.warn('Could not play MP3 file, falling back to beep:', mp3Error);
      try {
        await this.playNewOrderBeepSequence();
      } catch (beepError) {
        console.error('Error playing fallback beep notification:', beepError);
      }
    }
  }

  // Play urgent order notification
  async playUrgentOrderNotification(): Promise<void> {
    if (!this.enabled) return;

    try {
      // Play rapid beeps for urgent orders
      for (let i = 0; i < 3; i++) {
        await this.createBeepSound(1200, 0.1);
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    } catch (error) {
      console.error('Error playing urgent order notification:', error);
    }
  }

  // Play order completion notification
  async playOrderCompletionNotification(): Promise<void> {
    if (!this.enabled) return;

    try {
      // Play a pleasant completion sound
      await this.createBeepSound(600, 0.15);
      await new Promise(resolve => setTimeout(resolve, 50));
      await this.createBeepSound(800, 0.15);
      await new Promise(resolve => setTimeout(resolve, 50));
      await this.createBeepSound(1000, 0.3);
    } catch (error) {
      console.error('Error playing order completion notification:', error);
    }
  }

  // Play alert sound for attention
  async playAlertSound(): Promise<void> {
    if (!this.enabled) return;

    try {
      await this.createBeepSound(440, 0.5);
    } catch (error) {
      console.error('Error playing alert sound:', error);
    }
  }

  // Enable/disable sounds
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  // Request permission and initialize (needed for some browsers)
  async requestPermission(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch (error) {
        console.error('Failed to resume audio context:', error);
      }
    }
  }
}

// Export singleton instance
export const soundNotifications = SoundNotifications.getInstance();